import React, { useState, useRef, useEffect } from 'react';
import { Send, Trash2, Bot, User, Lightbulb, AlertTriangle, X, ThumbsUp, ThumbsDown, Flag } from 'lucide-react';

// WebSocket Configuration
const WS_URL = 'wss://1r5x85vspl.execute-api.us-west-2.amazonaws.com/production';

const ITOpsAdvisor = () => {
  const [showBetaModal, setShowBetaModal] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "👋 Hello! I'm your ITOps Advisor, an AI-powered IT operations assistant.\n\nI can help you with:\n\n• Troubleshooting IT infrastructure issues\n• Analyzing system dependencies and impacts\n• Providing resolution recommendations\n• Answering questions about IT operations best practices\n• Investigating root causes and patterns\n\nWhat can I help you with today?",
      id: 'welcome-msg'
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thinkingText, setThinkingText] = useState('');
  const [thinkingMessages, setThinkingMessages] = useState([]);
  const [showThinkingDetails, setShowThinkingDetails] = useState(true);
  const [streamingContent, setStreamingContent] = useState('');
  const [feedbackStates, setFeedbackStates] = useState({});
  const [reportForms, setReportForms] = useState({});
  const messagesEndRef = useRef(null);
  const sessionIdRef = useRef(null);
  const chatHistoryRef = useRef([]);
  const textareaRef = useRef(null);
  const wsRef = useRef(null);
  const isConnectingRef = useRef(false);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [input]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent, thinkingText, thinkingMessages]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Connect to WebSocket
  const connectWebSocket = () => {
    return new Promise((resolve, reject) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (isConnectingRef.current) {
        reject(new Error('Already connecting'));
        return;
      }

      isConnectingRef.current = true;
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[ITOps Advisor] ✅ WebSocket connected');
        isConnectingRef.current = false;
        resolve();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[ITOps Advisor] 📨 Message:', data.type);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('[ITOps Advisor] ❌ Parse error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[ITOps Advisor] ❌ WebSocket error:', error);
        isConnectingRef.current = false;
        reject(error);
      };

      ws.onclose = () => {
        console.log('[ITOps Advisor] 🔌 WebSocket closed');
        isConnectingRef.current = false;
      };
    });
  };

  // Handle WebSocket messages
  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'thinking':
        const thinkingMsg = {
          id: `think-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          content: data.content,
          timestamp: new Date().toISOString(),
          type: 'thinking'
        };
        setThinkingMessages(prev => [...prev, thinkingMsg]);
        setThinkingText(prev => prev ? prev + '\n' + data.content : data.content);
        break;
      case 'agent_thinking':
        const agentThinkingMsg = {
          id: `agent-think-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          content: data.content,
          timestamp: new Date().toISOString(),
          type: 'agent_thinking'
        };
        setThinkingMessages(prev => [...prev, agentThinkingMsg]);
        setThinkingText(prev => prev ? prev + '\n' + data.content : data.content);
        break;
      case 'stream':
        setStreamingContent(prev => prev + data.content);
        break;
      case 'complete':
        // Finalize the message
        const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const finalContent = data.completion || streamingContent;
        
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: finalContent,
          id: messageId
        }]);
        
        // Save to chat history
        chatHistoryRef.current.push({
          role: 'assistant',
          content: finalContent,
          timestamp: new Date().toISOString(),
          id: messageId
        });
        
        // Save to sessionStorage
        try {
          sessionStorage.setItem('itopsAdvisorChat', JSON.stringify(chatHistoryRef.current));
        } catch (e) {
          console.error('[ITOps Advisor] Error saving chat history:', e);
        }
        
        // Reset streaming state
        setStreamingContent('');
        setThinkingText('');
        setThinkingMessages([]);
        setIsTyping(false);
        setIsSubmitting(false);
        
        // Close WebSocket
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        break;
      case 'error':
        const errorMessage = `I encountered an error: ${data.content}. Please try again.`;
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: errorMessage,
          id: `error-${Date.now()}`
        }]);
        setStreamingContent('');
        setThinkingText('');
        setThinkingMessages([]);
        setIsTyping(false);
        setIsSubmitting(false);
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        break;
      default:
        break;
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isSubmitting) return;

    const userMessage = { role: 'user', content: input.trim(), id: `user-${Date.now()}` };
    const query = input.trim();
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    setIsSubmitting(true);
    setThinkingText('');
    setThinkingMessages([]);
    setStreamingContent('');

    // Save to chat history
    chatHistoryRef.current.push({ role: 'user', content: query, timestamp: new Date().toISOString() });

    // Generate or reuse session ID
    if (!sessionIdRef.current) {
      sessionIdRef.current = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    try {
      // Close existing WebSocket if open
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      await connectWebSocket();
      
      // Send the request
      wsRef.current.send(JSON.stringify({
        action: 'itops_advisor',
        inputText: query,
        sessionId: sessionIdRef.current
      }));
      
      console.log('[ITOps Advisor] 📤 Request sent');
    } catch (error) {
      console.error('[ITOps Advisor] Connection error:', error);
      
      const errorMessage = 'Failed to connect to the advisor service. Please try again.';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMessage,
        id: `error-${Date.now()}`
      }]);
      
      setIsTyping(false);
      setIsSubmitting(false);
    }
  };

  // Load chat history from sessionStorage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('itopsAdvisorChat');
      if (saved) {
        const history = JSON.parse(saved);
        chatHistoryRef.current = history;
        
        if (history.length > 0) {
          const restoredMessages = history.map(msg => ({
            role: msg.role,
            content: msg.content,
            id: msg.id
          }));
          setMessages(restoredMessages);
        }
      }
    } catch (e) {
      console.error('[ITOps Advisor] Error loading chat history:', e);
    }
  }, []);

  const handleClear = () => {
    if (!window.confirm('Are you sure you want to clear the chat history?')) return;

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Reset session
    sessionIdRef.current = null;
    chatHistoryRef.current = [];
    
    // Clear sessionStorage
    try {
      sessionStorage.removeItem('itopsAdvisorChat');
    } catch (e) {
      console.error('[ITOps Advisor] Error clearing chat history:', e);
    }

    // Reset state
    setStreamingContent('');
    setThinkingText('');
    setThinkingMessages([]);
    setIsTyping(false);
    setIsSubmitting(false);

    // Reset to welcome message
    setMessages([
      {
        role: 'assistant',
        content: "👋 Hello! I'm your ITOps Advisor, an AI-powered IT operations assistant.\n\nI can help you with:\n\n• Troubleshooting IT infrastructure issues\n• Analyzing system dependencies and impacts\n• Providing resolution recommendations\n• Answering questions about IT operations best practices\n• Investigating root causes and patterns\n\nWhat can I help you with today?",
        id: 'welcome-msg'
      }
    ]);

    console.log('[ITOps Advisor] Chat cleared, session reset');
  };

  const submitFeedback = async (messageId, feedbackType, description = '', isReport = false) => {
    console.log('[ITOps Advisor] Submitting feedback:', { messageId, feedbackType, description, isReport });
    setFeedbackStates(prev => ({ ...prev, [messageId]: { submitted: true, message: 'Thanks for your feedback!' } }));
  };

  const toggleReportForm = (messageId) => {
    setReportForms(prev => ({ ...prev, [messageId]: !prev[messageId] }));
  };

  const exampleQuestions = [
    "What's causing high latency in the API?",
    "Has this error happened before?",
    "How do we fix database connection issues?"
  ];

  return (
    <div className="h-full flex flex-col p-6 space-y-4">
      {/* Beta Modal */}
      {showBetaModal && (
        <>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" onClick={() => setShowBetaModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-card rounded-lg border-2 border-yellow-accent/60 max-w-2xl w-full relative"
                 style={{ 
                   background: '#141414',
                   boxShadow: '0 0 60px rgba(255, 223, 0, 0.3), inset 0 0 60px rgba(0, 0, 0, 0.5)'
                 }}>
              <button
                onClick={() => setShowBetaModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
              
              <div className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-yellow-accent/20 border-2 border-yellow-accent/60 flex items-center justify-center"
                       style={{ boxShadow: '0 0 20px rgba(255, 223, 0, 0.4)' }}>
                    <AlertTriangle size={24} className="text-yellow-accent" 
                                   style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
                  </div>
                  <h2 className="text-2xl font-bold text-yellow-accent"
                      style={{ textShadow: '0 0 20px #ffdf00, 0 0 40px #ffdf00' }}>
                    ⚠️ Beta Release
                  </h2>
                </div>
                
                <p className="text-white text-base mb-4 leading-relaxed">
                  Welcome to <span className="font-semibold text-orange-accent">ITOps Advisor (Beta)</span>. This AI-powered troubleshooting assistant is actively learning and improving.
                </p>
                
                <div className="bg-dark-border/40 border border-dark-border/60 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-semibold text-cyan-accent mb-3">What this means for you:</h3>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-accent mt-0.5">•</span>
                      <span>Recommendations should be treated as starting points, not final solutions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-accent mt-0.5">•</span>
                      <span>Always verify AI advice before taking action in production environments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-accent mt-0.5">•</span>
                      <span>All conversations are recorded and stored</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-accent mt-0.5">•</span>
                      <span>Your interactions help train and improve the AI</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-accent mt-0.5">•</span>
                      <span>Response quality will improve over time as we gather more data</span>
                    </li>
                  </ul>
                </div>
                
                <p className="text-xs text-gray-400 mb-6 leading-relaxed italic">
                  By using this feature, you acknowledge that your chat history will be used to enhance system performance and that you will exercise professional judgment when applying AI recommendations.
                </p>
                
                <button
                  onClick={() => setShowBetaModal(false)}
                  className="w-full py-3 rounded bg-orange-accent/20 border border-orange-accent/40 text-orange-accent hover:bg-orange-accent/30 transition-all font-semibold"
                  style={{ boxShadow: '0 0 25px rgba(255, 107, 53, 0.3)' }}
                >
                  I Understand - Continue to ITOps Advisor
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-orange-accent mb-2" 
              style={{ textShadow: '0 0 20px #ff6b35, 0 0 40px #ff6b35' }}>
            ITOps Advisor
          </h1>
          <p className="text-sm text-gray-400">AI-powered IT operations assistant with streaming responses</p>
        </div>
        <button
          onClick={handleClear}
          className="flex items-center gap-2 px-4 py-2 rounded bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-orange-accent hover:border-orange-accent/40 transition-all text-sm font-semibold"
        >
          <Trash2 size={16} />
          Clear Chat
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-dark-card rounded-lg border border-dark-border p-4"
           style={{ 
             background: '#141414',
             boxShadow: '0 0 30px rgba(255, 107, 53, 0.1), inset 0 0 40px rgba(0, 0, 0, 0.5)'
           }}>
        <div className="flex items-start gap-3">
          <Lightbulb size={20} className="text-yellow-accent mt-0.5 flex-shrink-0" 
                     style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">How to use ITOps Advisor</h3>
            <p className="text-xs text-gray-400 mb-2">
              Ask questions about IT operations, incidents, system dependencies, or resolution steps. 
              The AI assistant leverages Bedrock Agent capabilities to provide intelligent responses and recommendations.
            </p>
            <p className="text-xs text-gray-500 mt-2 italic">
              Powered by AWS Bedrock Agent with WebSocket streaming
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {exampleQuestions.map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => setInput(question)}
                  className="text-xs px-3 py-1 rounded bg-dark-border/40 border border-orange-accent/30 text-orange-accent hover:bg-orange-accent/10 transition-all"
                >
                  "{question}"
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div className="flex-1 bg-dark-card rounded-lg border border-dark-border overflow-hidden flex flex-col"
           style={{ 
             background: '#0d0d0d',
             boxShadow: '0 0 40px rgba(255, 107, 53, 0.1), inset 0 0 60px rgba(0, 0, 0, 0.5)'
           }}>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((message, index) => (
            <div key={index} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {message.role === 'assistant' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-accent/20 border border-orange-accent/40 flex items-center justify-center"
                     style={{ boxShadow: '0 0 15px rgba(255, 107, 53, 0.3)' }}>
                  <Bot size={16} className="text-orange-accent" />
                </div>
              )}
              
              <div className={`max-w-2xl rounded-lg p-4 ${
                message.role === 'user'
                  ? 'bg-cyan-accent/10 border border-cyan-accent/30 text-white'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-200'
              }`}
                   style={message.role === 'user' ? { boxShadow: '0 0 20px rgba(0, 229, 255, 0.15)' } : {}}>
                <p className="text-sm whitespace-pre-line leading-relaxed">{message.content}</p>
                
                {/* Feedback Component for Assistant Messages */}
                {message.role === 'assistant' && message.id && message.id !== 'welcome-msg' && !message.id.startsWith('error-') && (
                  <div className="mt-3 pt-3 border-t border-dark-border/30">
                    {!feedbackStates[message.id]?.submitted ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-400">Was this helpful?</span>
                        <button
                          onClick={() => submitFeedback(message.id, 'positive')}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all text-green-400 border border-green-500/30 hover:bg-green-500/20 hover:border-green-500/50"
                        >
                          <ThumbsUp size={12} />
                          Helpful
                        </button>
                        <button
                          onClick={() => submitFeedback(message.id, 'negative')}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all text-red-400 border border-red-500/30 hover:bg-red-500/20 hover:border-red-500/50"
                        >
                          <ThumbsDown size={12} />
                          Not Helpful
                        </button>
                        <button
                          onClick={() => toggleReportForm(message.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all text-red-400 border border-red-500/30 hover:text-red-300 hover:bg-red-500/20 hover:border-red-500/50"
                        >
                          <Flag size={12} />
                          Report
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs text-green-400">
                        {feedbackStates[message.id]?.message || 'Thanks for your feedback!'}
                      </div>
                    )}
                    
                    {reportForms[message.id] && (
                      <div className="mt-2 space-y-2">
                        <textarea
                          id={`issue-description-${message.id}`}
                          placeholder="Describe the issue..."
                          className="w-full px-2 py-1.5 bg-dark-card border border-dark-border rounded text-xs text-white resize-none"
                          rows={2}
                        />
                        <button
                          onClick={() => {
                            const description = document.getElementById(`issue-description-${message.id}`)?.value || '';
                            submitFeedback(message.id, 'negative', description, true);
                            toggleReportForm(message.id);
                          }}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium transition-all"
                        >
                          Submit Report
                        </button>
                      </div>
                    )}
                    
                    {feedbackStates[message.id]?.error && (
                      <div className="mt-2 text-xs text-red-400">
                        {feedbackStates[message.id].error}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {message.role === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-accent flex items-center justify-center"
                     style={{ boxShadow: '0 0 15px rgba(0, 229, 255, 0.5)' }}>
                  <User size={16} className="text-black" />
                </div>
              )}
            </div>
          ))}
          
          {/* Streaming Response */}
          {isTyping && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-accent/20 border border-orange-accent/40 flex items-center justify-center"
                   style={{ boxShadow: '0 0 15px rgba(255, 107, 53, 0.3)' }}>
                <Bot size={16} className="text-orange-accent" />
              </div>
              <div className="max-w-2xl bg-dark-border/40 border border-dark-border/60 rounded-lg p-4 space-y-3">
                {/* Thinking indicator - enhanced display */}
                {(thinkingMessages.length > 0 || thinkingText) && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3"
                       style={{ boxShadow: '0 0 15px rgba(168, 85, 247, 0.15)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                        <span className="text-xs font-semibold text-purple-400">
                          AI Thinking Process {thinkingMessages.length > 0 && `(${thinkingMessages.length} steps)`}
                        </span>
                      </div>
                      {thinkingMessages.length > 0 && (
                        <button
                          onClick={() => setShowThinkingDetails(!showThinkingDetails)}
                          className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                        >
                          {showThinkingDetails ? 'Hide Details' : 'Show Details'}
                        </button>
                      )}
                    </div>
                    {showThinkingDetails && (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {thinkingMessages.length > 0 ? (
                          thinkingMessages.map((msg, idx) => (
                            <div 
                              key={msg.id} 
                              className="text-xs text-purple-300/90 leading-relaxed border-l-2 border-purple-500/40 pl-2 py-1 bg-purple-500/5 rounded"
                            >
                              <div className="flex items-start gap-2">
                                <span className="text-purple-400/60 font-mono text-[10px] mt-0.5 flex-shrink-0">
                                  {idx + 1}.
                                </span>
                                <span className="flex-1">{msg.content}</span>
                              </div>
                            </div>
                          ))
                        ) : thinkingText ? (
                          <div className="text-xs text-purple-300 font-mono whitespace-pre-line leading-relaxed">
                            {thinkingText}
                          </div>
                        ) : null}
                      </div>
                    )}
                    {!showThinkingDetails && thinkingMessages.length > 0 && (
                      <div className="text-xs text-purple-400/70 italic">
                        {thinkingMessages[thinkingMessages.length - 1]?.content || 'Processing...'}
                      </div>
                    )}
                  </div>
                )}
                {/* Streaming content */}
                {streamingContent ? (
                  <div>
                    {thinkingText && <div className="border-t border-dark-border/30 my-2" />}
                    <p className="text-sm text-gray-200 whitespace-pre-line leading-relaxed">
                      {streamingContent}
                      <span className="inline-block w-2 h-3 bg-orange-accent ml-1 animate-pulse" />
                    </p>
                  </div>
                ) : !thinkingText && (
                  <div className="flex gap-1 items-center">
                    <span className="text-xs text-gray-500 mr-2">Connecting...</span>
                    <div className="w-2 h-2 rounded-full bg-orange-accent animate-pulse" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 rounded-full bg-orange-accent animate-pulse" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 rounded-full bg-orange-accent animate-pulse" style={{ animationDelay: '300ms' }} />
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-dark-border/50 p-4">
          <div className="flex gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask about incidents, errors, or resolutions..."
              className="flex-1 px-4 py-3 bg-dark-card border border-dark-border rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-accent/60 transition-all resize-none"
              style={{ 
                boxShadow: '0 0 20px rgba(255, 107, 53, 0.1)',
                minHeight: '48px',
                maxHeight: '150px'
              }}
              rows={1}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isSubmitting}
              className="px-6 py-3 rounded bg-orange-accent/20 border border-orange-accent/40 text-orange-accent hover:bg-orange-accent/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all font-semibold text-sm flex items-center gap-2"
              style={{ boxShadow: '0 0 20px rgba(255, 107, 53, 0.2)' }}
            >
              <Send size={16} />
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ITOpsAdvisor;
